import React from "react";
import { ColumnDef, flexRender, getCoreRowModel, getSortedRowModel, getFilteredRowModel, SortingState, ColumnFiltersState, useReactTable, RowData } from "@tanstack/react-table";
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/shadcn/table";
import { cn } from "../../utils/utilsShared";
import { ChevronUp, ChevronDown, ChevronsUpDown, ChevronRight } from "lucide-react";
import { Skeleton } from "../../components/loading/Skeleton";



interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  title?: string;
  description?: string;
  showTableLength?: boolean;
  className?: string;
  enableSorting?: boolean;
  enableFiltering?: boolean;
  pageSize?: number;
  showHeader?: boolean;
  isLoading?: boolean;
  enableExpansion?: boolean;
  pagesize?: number;
  height?: number | string;
  expandableRowRenderer?: (rowData: TData) => React.ReactNode;
  getRowCanExpand?: (row: TData) => boolean;
}

const DataTable = <TData, TValue>({
  columns,
  data,
  title,
  description,
  showTableLength = true,
  className,
  enableSorting = true,
  enableFiltering = false,
  pageSize = 10,
  showHeader = true,
  isLoading = false,
  enableExpansion = false,
  height,
  expandableRowRenderer,
  getRowCanExpand,
}: DataTableProps<TData, TValue>) => {

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  const [expandedRows, setExpandedRows] = React.useState<Set<number>>(new Set());

  const safeData = (Array.isArray(data) ? data : []) as TData[];

  const table = useReactTable({
    data: safeData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
    getFilteredRowModel: enableFiltering ? getFilteredRowModel() : undefined,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    state: {
      sorting,
      columnFilters,
    },
    initialState: {
      pagination: {
        pageSize,
      },
    },
  });

  const toggleRow = (rowIndex: number) => {
    const newExpandedRows = new Set(expandedRows);
    if (expandedRows.has(rowIndex)) {
      newExpandedRows.delete(rowIndex);
    } else {
      newExpandedRows.add(rowIndex);
    }
    setExpandedRows(newExpandedRows);
  };

  const handleSort = (columnId: string) => {
    if (!enableSorting) return;

    const column = table.getColumn(columnId);
    if (!column) return;

    const currentSort = column.getIsSorted();
    if (currentSort === false) {
      column.toggleSorting(false);
    } else if (currentSort === "asc") {
      column.toggleSorting(true);
    } else {
      column.clearSorting();
    }
  };

  const getSortIcon = (columnId: string) => {
    if (!enableSorting) return null;

    const column = table.getColumn(columnId);
    if (!column || !column.getCanSort()) return null;

    const isSorted = column.getIsSorted();

    if (isSorted === "asc") {
      return <ChevronUp className="h-4 w-4 text-gray-600" />;
    } else if (isSorted === "desc") {
      return <ChevronDown className="h-4 w-4 text-gray-600" />;
    } else {
      return <ChevronsUpDown className="h-4 w-4 text-gray-400" />;
    }
  };

  const canRowExpand = (rowData: TData) => {
    if (!enableExpansion || !expandableRowRenderer) return false;
    if (getRowCanExpand && !getRowCanExpand(rowData)) return false;
    return true;
  };

  return (
    <div className={cn("w-full space-y-4", className)}>
      {showHeader && (
        <div className="flex flex-col ">
          <div className="flex items-center space-x-2">
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            {showTableLength && (
              <span className="text-sm text-gray-500">
                {(table.getFilteredRowModel()?.rows?.length ?? 0)} total rows
              </span>
            )}
          </div>
          {description && (
            <span className="text-sm text-gray-500">{description}</span>
          )}
        </div>
      )}

      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div
          className={cn(height != null && "overflow-y-auto")}
          style={
            height != null
              ? { maxHeight: typeof height === "number" ? `${height}px` : height }
              : undefined
          }
          aria-label={height != null ? "Scrollable table body" : undefined}
        >
          <table className="w-full caption-bottom text-sm border-collapse">
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow
                  key={headerGroup.id}
                  className={cn(
                    "bg-gray-50",
                    height != null &&
                      "sticky top-0 z-10 bg-gray-50 shadow-[0_1px_0_0_rgba(0,0,0,0.05)]"
                  )}
                >
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      className={cn(
                        "whitespace-nowrap bg-gray-50",
                        height != null && "sticky top-0 z-10",
                        enableSorting &&
                          header.column.getCanSort() &&
                          "cursor-pointer select-none"
                      )}
                      onClick={() =>
                        header.column.getCanSort() && handleSort(header.column.id)
                      }
                    >
                      <div className="flex items-center space-x-2">
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                        {getSortIcon(header.column.id)}
                      </div>
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: pageSize }).map((_, index) => (
                  <TableRow key={`loading-row-${index}`}>
                    {Array.from({ length: columns.length }).map((_, cellIndex) => (
                      <TableCell key={`loading-cell-${cellIndex}`} className="py-4">
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row, rowIndex) => {
                  const isExpandable = canRowExpand(row.original);
                  const isExpanded = expandedRows.has(rowIndex);

                  return (
                    <React.Fragment key={row.id}>
                      <TableRow
                        className={cn(
                          "border-b border-gray-100",
                          isExpandable &&
                            "cursor-pointer hover:bg-gray-50 transition-colors",
                          isExpanded && "bg-white"
                        )}
                        data-state={row.getIsSelected() && "selected"}
                        onClick={() => {
                          if (isExpandable) toggleRow(rowIndex);
                        }}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>
                            <div className="flex items-center justify-between">
                              <div>
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </div>
                              {isExpandable &&
                                cell.id ===
                                  row.getVisibleCells()[
                                    row.getVisibleCells().length - 1
                                  ].id && (
                                  <ChevronRight
                                    className={cn(
                                      "h-4 w-4 text-gray-400 transition-transform duration-200 ml-2",
                                      isExpanded && "rotate-90"
                                    )}
                                  />
                                )}
                            </div>
                          </TableCell>
                        ))}
                      </TableRow>
                      {enableExpansion &&
                        isExpanded &&
                        expandableRowRenderer && (
                          <TableRow>
                            <TableCell
                              colSpan={columns.length}
                              className="p-0 bg-gray-50"
                            >
                              <div className="p-4">
                                {expandableRowRenderer(row.original)}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                    </React.Fragment>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center text-gray-500"
                  >
                    No results found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </table>
        </div>
      </div>


    </div>
  );
};

export default DataTable;
